import React from 'react';
import { makeStyles } from 'tss-react/mui';

import {
    AppBar,
    Toolbar,
    Box,
    Button,
    IconButton,
    Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AssessmentIcon from '@mui/icons-material/Assessment';

import { Link as RouterLink } from 'react-router-dom';
import { isFeatureEnabled, FEATURE } from 'config';

import { useAppSelector } from 'hooks';
import AccountMenu from 'components/AccountMenu';

const useStyles = makeStyles()(() => ({
    root: {
        flexGrow: 1,
        zIndex: 1201,
        height: 'fit-content',
    },
}));

interface NavBarProps {
    menuLocation?: string;
    style?: React.CSSProperties;
    className?: string;
}

const NavBar: React.FunctionComponent<NavBarProps> = ({ style, className }) => {
    const { classes } = useStyles();

    const {
        auth,
        guide: { selectedPlanType },
    } = useAppSelector((rootState) => rootState);

    const { tokens, user } = auth;

    let loginStatus = 'Not Logged In';
    if (user.status === 'loading' || tokens.status === 'loading') {
        loginStatus = 'Logging In...';
    } else if (user.status === 'finished' && user.object) {
        loginStatus = user.object.fullName;
    }

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const titleHref = (() => {
        if (
            window.location.href.includes('/gis') &&
            selectedPlanType !== undefined &&
            isFeatureEnabled(FEATURE.FAFT_UI)
        ) {
            return '/plan';
        }
        return '/';
    })();

    return (
        <div className={`${classes.root} ${className ?? ''}`} style={style}>
            <AppBar
                position="static"
                sx={{
                    backgroundColor: 'white',
                    color: 'text.primary',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Toolbar
                    sx={{
                        maxWidth: 1280,
                        width: '100%',
                        mx: 'auto',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 2 },
                    }}
                >
                    <Box
                        component={RouterLink}
                        to={titleHref}
                        title="Navigate Home"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 2 },
                            textDecoration: 'none',
                            flexGrow: 1,
                        }}
                    >
                        <Box
                            component="img"
                            src="/logo-bip.png"
                            alt="State Bushfire Coordination Committee"
                            sx={{
                                height: { xs: 48, sm: 56, md: 64 },
                                width: 'auto',
                            }}
                        />
                        <Typography
                            variant="h5"
                            component="span"
                            sx={{
                                color: 'primary.main',
                                fontSize: {
                                    xs: '1rem',
                                    sm: '1.25rem',
                                    md: '1.5rem',
                                },
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            BIP
                        </Typography>

                        <Box
                            sx={{
                                ml: { xs: 1, sm: 2 },
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 0.5, sm: 0.75 },
                                backgroundColor: '#D32F2F',
                                color: 'white',
                                fontSize: {
                                    xs: '0.75rem',
                                    sm: '0.875rem',
                                    md: '1rem',
                                },
                                fontWeight: 900,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                borderRadius: 1,
                                boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
                            }}
                        >
                            PILOT
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0, sm: 1 },
                        }}
                    >
                        <Button
                            startIcon={<AssessmentIcon />}
                            disableRipple
                            sx={{
                                color: 'text.disabled',
                                textTransform: 'none',
                                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                px: { xs: 1, sm: 2 },
                                cursor: 'not-allowed',
                                '&:hover': { backgroundColor: 'transparent' },
                                display: { xs: 'none', sm: 'flex' },
                            }}
                        >
                            Dashboard/Reports
                        </Button>

                        <IconButton
                            onClick={handleMenuOpen}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'text.primary' },
                            }}
                            title={tokens.object?.decodedJWT.email}
                        >
                            <AccountCircleIcon />
                        </IconButton>
                        <Typography
                            variant="body2"
                            onClick={handleMenuOpen}
                            sx={{
                                cursor: 'pointer',
                                color: 'text.secondary',
                                '&:hover': { color: 'text.primary' },
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            {loginStatus}
                        </Typography>

                        <AccountMenu
                            anchorEl={anchorEl}
                            open={isMenuOpen}
                            onClose={handleMenuClose}
                            paperSx={{
                                mt: 1,
                                minWidth: 192,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            }}
                        />
                    </Box>
                </Toolbar>
            </AppBar>
        </div>
    );
};

export default NavBar;
